import React, { useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

// Second login step for therapists with 2FA enabled. The first step
// (Login.handleSubmit) verified the password and received a short-lived
// `mfa_token`; here we exchange that token + the authenticator code for the
// real session payload. Uses fetch (like loginUser) so the http 401-logout
// interceptor does not interfere with the challenge.
async function verifyOtp(mfaToken, code) {
  return fetch(
    process.env.REACT_APP_SOPHYAPP_API_URL + "/de/v2/account/verify_otp",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ mfa_token: mfaToken, otp_code: code }),
    }
  ).then((res) => res.json());
}

export default function OtpChallengeForm({ mfaToken, onVerified, onBack }) {
  const { t } = useTranslation();
  const [code, setCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setFailed(false);
    try {
      const res = await verifyOtp(mfaToken, code.trim());
      if (res && res.access_token) {
        onVerified(res);
      } else {
        setFailed(true);
      }
    } catch {
      setFailed(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="lv2-form-container">
      <form onSubmit={handleSubmit} noValidate>
        <p className="lv2-register-text" style={{ marginBottom: "12px" }}>
          {t("twoFactor.challengePrompt", {
            defaultValue:
              "Gib den 6-stelligen Code aus deiner Authenticator-App ein.",
          })}
        </p>
        <div className="lv2-input-wrapper">
          <input
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder={t("twoFactor.codePlaceholder", {
              defaultValue: "6-stelliger Code",
            })}
            className="lv2-input"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            autoFocus
            required
          />
        </div>
        <div className="lv2-submit-wrapper">
          <button
            type="submit"
            className="lv2-submit-btn"
            disabled={isLoading || !code.trim()}
          >
            {isLoading
              ? t("actions.loading", { defaultValue: "Lädt..." })
              : t("twoFactor.verify", { defaultValue: "Bestätigen" })}
          </button>
        </div>

        {failed && (
          <p className="lv2-register-text" style={{ color: "#c0392b" }}>
            {t("twoFactor.challengeFailed", {
              defaultValue:
                "Code ungültig oder abgelaufen. Bitte versuche es erneut.",
            })}
          </p>
        )}

        <p
          className="lv2-register-text"
          style={{ marginTop: "12px", fontSize: "0.85em" }}
        >
          {t("twoFactor.backupHint", {
            defaultValue:
              "Kein Zugriff auf die App? Du kannst stattdessen einen deiner Backup-Codes eingeben.",
          })}
        </p>

        <div className="lv2-forgot-wrapper">
          <span
            className="lv2-forgot-link"
            onClick={onBack}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && onBack()}
          >
            {t("twoFactor.backToLogin", { defaultValue: "Zurück zur Anmeldung" })}
          </span>
        </div>
      </form>
    </div>
  );
}

OtpChallengeForm.propTypes = {
  mfaToken: PropTypes.string.isRequired,
  onVerified: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
};
